export function formatMoney(currency: string, amount: number | string | null | undefined): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount || 0))
}

const THOUSAND = 1_000
const MILLION = 1_000_000
const BILLION = 1_000_000_000

// Abbreviate large amounts by 3-digit groups (FS-10: "AED 500K", not "500.00k").
const COMPACT_TIERS: { divisor: number; suffix: string }[] = [
  { divisor: BILLION, suffix: 'BN' },
  { divisor: MILLION, suffix: 'M' },
  { divisor: THOUSAND, suffix: 'K' },
]

/** Up to two decimals with trailing zeros dropped — 500 → "500", 2.5 → "2.5". */
function trimToTwoDecimals(value: number): string {
  return String(Number(value.toFixed(2)))
}

/**
 * Compact currency for scannable surfaces — "AED 500K", "AED 2.5M",
 * "AED 1.25BN". At most two decimals, trailing zeros dropped. Amounts below
 * 1,000 render unabbreviated ("AED 250"). The tier is picked after rounding,
 * so 999,999 reads "AED 1M" rather than "AED 1000K". For exact ledger values
 * (transactions, wallet balances) use {@link formatMoney} instead — this form
 * trades precision for scannability.
 */
export function formatCompactMoney(
  currency: string,
  amount: number | string | null | undefined,
): string {
  const value = Number(amount ?? 0)
  if (!Number.isFinite(value)) return `${currency} 0`

  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const tier = COMPACT_TIERS.find((t) => Number((abs / t.divisor).toFixed(2)) >= 1)
  const scaled = tier ? abs / tier.divisor : abs

  return `${currency} ${sign}${trimToTwoDecimals(scaled)}${tier?.suffix ?? ''}`
}
